import { useCallback, useEffect, useRef, useState } from "react";
import {
  StatusCodeFilter,
  statusFilterKey,
} from "../statusCodeFilter";
import { RouteRenderMode, TimedPathPoint } from "./resolveRoutePath";
import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { APP_CONFIG } from "../../../constants/constants";
import { SPEED_UNITS } from "../../admin-details/account/constants";
import { tripDisplayNumberOr, tripDisplayNumbers } from "./tripNumbering";
import { TMapRef } from "../type";
import { setLegacyRouteRenderingSuppressed } from "../map-elements/helper";
import { clearLegacyMapLayer } from "./clearLegacyLayer";
import {
  renderMultiDayRoutes,
  SelectedDayTrip,
  TripPointClickSource,
} from "./renderMultiDayRoutes";
import { DEFAULT_REPLAY_SPEED } from "./ReplayControlsPanel";
import { resolveRouteLineStyle } from "./routeLineStyle";
import { overlayRouteModes } from "./routeViewModes";
import { createRouteOverlayLines } from "./routeOverlayLines";
import { createTooltipController } from "./tooltip";
import { isNonTrip } from "./tripClassification";
import { DateGroup } from "./useMultiDateTripData";

// Progress is React state on the Asset Map, and each update redraws that
// page - ~60ms a time. Called on every animation step, the redraws set the
// pace instead of the speed buttons: 10x came out nearer 3-4x. So progress
// reaches the page at most this often; the draw itself is untouched.
const PROGRESS_REPORT_INTERVAL_MS = 200;

type ProgressReport = (
  percent: number,
  clock?: number,
  isFastForward?: boolean,
  speedKph?: number
) => void;

// passes the latest report on at most once per interval, and always the
// final one. `flush` pushes out whatever is held back, so a pause freezes
// the panel on the step the draw actually stopped at.
const throttleProgress = (report: ProgressReport) => {
  let lastReportedAt = -Infinity;
  let pending: Parameters<ProgressReport> | null = null;
  const flush = () => {
    if (!pending) return;
    lastReportedAt = performance.now();
    const args = pending;
    pending = null;
    report(...args);
  };
  const onProgress: ProgressReport = (...args) => {
    pending = args;
    if (
      args[0] >= 100 ||
      performance.now() - lastReportedAt >= PROGRESS_REPORT_INTERVAL_MS
    )
      flush();
  };
  return { onProgress, flush };
};

type RenderHandle = {
  clear: () => void;
  fitBoundsIfAny: () => void;
  playDrawInAnimation: (
    getSpeedMultiplier?: () => number,
    onProgress?: (
      percent: number,
      clock?: number,
      isFastForward?: boolean
    ) => void
  ) => Promise<void>;
  playTimelineReplay: (
    getSpeedMultiplier: () => number,
    onProgress?: (
      percent: number,
      clock?: number,
      isFastForward?: boolean
    ) => void
  ) => Promise<void>;
  stopTimelineReplay: () => void;
  playDetailAnimation: (
    tripKey: string,
    getSpeedMultiplier: () => number,
    onProgress?: (
      percent: number,
      clock?: number,
      isFastForward?: boolean,
      speedKph?: number
    ) => void
  ) => Promise<void>;
  resetDetailPinsForReplay: () => void;
  stopDetailAnimation: () => void;
  pauseReplay: () => void;
  resumeReplay: () => void;
  setHighlightedTrip: (tripKey: string | null) => void;
  setLineStyle: (lineStyle: ReturnType<typeof resolveRouteLineStyle>) => void;
  setDetailedTrip: (
    tripKey: string | null,
    filteredStatusCodes: StatusCodeFilter,
    showPinsImmediately?: boolean
  ) => void;
  highlightEventPoint: (point: VehicleMapDataPoint | null) => void;
  updateTripPath: (
    tripKey: string,
    path: TimedPathPoint[],
    immediate?: boolean
  ) => void;
  setOverlapsEnabled: (enabled: boolean) => void;
  fitBoundsToTrip: (tripKey: string) => Promise<void>;
  fitBoundsToTrips: (tripKeys: string[]) => Promise<void>;
  closeLens: () => void;
};

// renders every checked trip across the selected dates, coloured by day.
// only active in multi-day mode; single-date keeps the existing pipeline.
export const useMultiDayMapRender = ({
  mapRef,
  dateGroups,
  selectedTripKeys,
  enabled,
  isDataReady,
  mapInstanceVersion = 0,
  routeViewsVersion,
  paused = false,
  hoveredTripKey = null,
  onMapHoverTrip,
  includeNonTrips = false,
  detailedTripKey = null,
  filteredStatusCodes = null,
  resolvedPathsRef,
  overlapsEnabled = true,
  onTripPointClick,
  assetKey,
  speedUnit,
  timezone,
}: {
  mapRef: React.MutableRefObject<TMapRef>;
  dateGroups: DateGroup[];
  // road-aligned paths, filled in as the active route view resolves them.
  // a ref rather than state: a re-render per resolved trip would rebuild
  // the whole map. Absent entries just mean "still on the raw fixes".
  resolvedPathsRef?: React.MutableRefObject<Map<string, TimedPathPoint[]>>;
  selectedTripKeys: Set<string>;
  enabled: boolean;
  includeNonTrips?: boolean;
  // the trip whose detail is open - shows its event pins and focuses it
  detailedTripKey?: string | null;
  // event types to pin for the detailed trip, from the Event Filter.
  // null = not applied (pin everything); [] = pin nothing
  filteredStatusCodes?: StatusCodeFilter;
  // on by default - one asset over at most 7 days is cheap to detect, and
  // days retracing the same route is the point of this view
  overlapsEnabled?: boolean;
  onTripPointClick?: (
    tripKey: string,
    point: VehicleMapDataPoint,
    source?: TripPointClickSource
  ) => void;
  // which asset these trips belong to. Trip keys here are dateKey__index
  // with no asset in them (unlike the multi-vehicle map, whose keys carry
  // the deviceId), so without this a different asset's trips on the same
  // dates produce an identical signature and the redraw below reads as
  // "same thing already on screen" - no re-frame, and the new routes draw
  // wherever the camera happened to be left.
  assetKey?: string;
  // the account's configured speed unit, for the hover tooltip
  speedUnit?: SPEED_UNITS;
  // the account's timezone, for the stacked-event list's times. Read when
  // the renderer is built, so a change mid-session lands on the next redraw.
  timezone?: string;
  // true once every selected date's fetch has settled - drawing on partial
  // arrivals would replay the intro animation once per date
  isDataReady: boolean;
  // changes whenever BasicMap builds a new google.maps.Map. Everything this
  // hook drew belonged to the old one, so a change here is a full restart,
  // not a redraw - see the reset at the top of the draw effect.
  mapInstanceVersion?: number;
  // bumped by the Route View toggle so the lines pick up the new style
  routeViewsVersion: number;
  // true while a single-trip replay has the map to itself
  paused?: boolean;
  hoveredTripKey?: string | null;
  onMapHoverTrip?: (tripKey: string | null) => void;
}) => {
  const cleanupRef = useRef<() => void>(() => {});
  const renderHandleRef = useRef<RenderHandle | null>(null);
  // outlives a redraw: the resolved comparison geometry is expensive and
  // the layer caches it, so a redraw reuses what it already has
  const overlayLinesRef = useRef<ReturnType<
    typeof createRouteOverlayLines
  > | null>(null);
  const hasRenderedRef = useRef(false);
  const selectedTripsSignatureRef = useRef<string | null>(null);
  const drawnOnMapInstanceRef = useRef(mapInstanceVersion);
  // undefined until the first draw, so arriving on an asset is told apart
  // from switching to one - see the empty-view fallback in the draw effect
  const drawnAssetKeyRef = useRef<string | undefined>(undefined);
  const [isAnimating, setIsAnimating] = useState(false);
  // set only by an explicit Replay All, unlike isAnimating which the
  // initial draw-in also sets
  const [isReplayingAll, setIsReplayingAll] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState<number>(DEFAULT_REPLAY_SPEED);
  const replaySpeedRef = useRef(replaySpeed);
  // the running animation's held-back progress, pushed out on pause
  const flushProgressRef = useRef<() => void>(() => {});
  replaySpeedRef.current = replaySpeed;
  // the ref as well as the state: an animation starting in this same tick
  // reads the ref before React has re-rendered
  const resetSpeed = () => {
    replaySpeedRef.current = DEFAULT_REPLAY_SPEED;
    setReplaySpeed(DEFAULT_REPLAY_SPEED);
  };
  const [replayProgress, setReplayProgress] = useState<number | null>(null);
  // the initial draw-in's own progress, kept apart from Replay All's so the
  // controls panel can tell which of the two it is sitting over
  const [drawProgress, setDrawProgress] = useState<number | null>(null);
  // the universal clock: the real timestamp Replay All is currently laying
  // down - see ReplayControlsPanel and the multi-vehicle map's mapRender
  // for the same idea
  const [replayClock, setReplayClock] = useState<number | null>(null);
  // true while the clock is racing through a stretch nothing was reported
  // in - the panel says so rather than letting the time appear to glitch
  const [isFastForwarding, setIsFastForwarding] = useState(false);
  // detail-view route draw, tracked separately from the replay flags so
  // the two don't fight over the controls panel
  const [isDetailAnimating, setIsDetailAnimating] = useState(false);
  const [detailProgress, setDetailProgress] = useState<number | null>(null);
  const [detailClock, setDetailClock] = useState<number | null>(null);
  // the asset's reported speed at the moment on the detail clock. One trip
  // is one asset, so the detail draw - unlike the whole-map draws - has a
  // speed worth putting next to the time.
  const [detailSpeedKph, setDetailSpeedKph] = useState<number | null>(null);
  // the open trip's route has been drawn all the way through (or skipped to
  // the end). Without this the panel reads as "finished" for the frame
  // between a trip opening and its draw effect running, flashing a Replay
  // button over a route that has not drawn yet.
  const [detailDrawComplete, setDetailDrawComplete] = useState(false);
  // one flag covers both Replay All and the detail draw - the renderer's
  // pause gate is shared between them since only one ever runs
  const [isPaused, setIsPaused] = useState(false);

  // read in the draw effect without being deps of it, so opening a trip's
  // detail doesn't retrigger a full redraw
  const detailedTripKeyRef = useRef(detailedTripKey);
  detailedTripKeyRef.current = detailedTripKey;
  const filteredStatusCodesRef = useRef(filteredStatusCodes);
  filteredStatusCodesRef.current = filteredStatusCodes;
  const overlapsEnabledRef = useRef(overlapsEnabled);
  overlapsEnabledRef.current = overlapsEnabled;

  // keep the legacy single-day layer off the map while multi-day is on
  useEffect(() => {
    // stops the legacy renderers attaching new polylines - covers the
    // snap-to-road / directions renders that were kicked off before the
    // switch and only land once their API calls return
    setLegacyRouteRenderingSuppressed(enabled);

    if (!enabled) {
      return;
    }

    // clear what was already drawn; the retries cover stragglers
    clearLegacyMapLayer(mapRef);
    const burstTimers = [150, 600, 1500].map((delay) =>
      setTimeout(() => clearLegacyMapLayer(mapRef), delay)
    );

    return () => {
      burstTimers.forEach(clearTimeout);
      // module-level flag, so release it or the next single-day view
      // renders nothing
      setLegacyRouteRenderingSuppressed(false);
    };
  }, [mapRef, enabled]);

  useEffect(() => {
    // A new google.maps.Map means every polyline, marker and listener we
    // created is gone with the old one. Both of these are what make the
    // redraw below a no-op - hasRenderedRef suppresses the fitBounds and the
    // signature match suppresses it again - so clear them before they are
    // read, and before the early return, which must not swallow the reset.
    if (drawnOnMapInstanceRef.current !== mapInstanceVersion) {
      drawnOnMapInstanceRef.current = mapInstanceVersion;
      hasRenderedRef.current = false;
      selectedTripsSignatureRef.current = null;
    }

    const map = mapRef.current?.map;
    if (!map || !enabled || !isDataReady || paused) {
      cleanupRef.current();
      cleanupRef.current = () => {};
      return;
    }

    const selectedTripsSignature = `${assetKey ?? ""}::${Array.from(
      selectedTripKeys
    )
      .sort()
      .join("|")}`;
    const shouldFitBounds =
      !hasRenderedRef.current ||
      selectedTripsSignatureRef.current !== selectedTripsSignature;
    selectedTripsSignatureRef.current = selectedTripsSignature;

    // CAUSE 1 of "the map stays on the previous asset": every camera path
    // here frames something or does nothing - fitBoundsIfAny returns early
    // on an empty set, by name and by design. So an asset with no trips on
    // these dates left the view sitting exactly where the previous asset
    // had driven, which reads as "this is where it went".
    //
    // Keyed on the asset having nothing rather than on nothing being framed:
    // the page's auto-select runs a render ahead of this effect, so the
    // first pass after new data lands still holds the previous selection and
    // frames nothing either. Backing the camera out on that would flash the
    // default view and come straight back.
    const assetChanged =
      drawnAssetKeyRef.current !== undefined &&
      drawnAssetKeyRef.current !== assetKey;
    drawnAssetKeyRef.current = assetKey;
    const assetHasNoTrips = dateGroups.every(
      (group) => group.trips.length === 0
    );
    if (assetChanged && assetHasNoTrips) {
      // the same view the map opens on when there is nothing to focus on
      map.setCenter({
        lat: APP_CONFIG.MAPS.DEFAULTS.CENTER.latitude,
        lng: APP_CONFIG.MAPS.DEFAULTS.CENTER.longitude,
      });
      map.setZoom(APP_CONFIG.MAPS.DEFAULTS.ZOOM);
    }

    cleanupRef.current();
    cleanupRef.current = () => {};

    const trips: SelectedDayTrip[] = [];
    dateGroups.forEach((group) => {
      // the same numbering the trip list shows, so a route's tooltip and
      // its row agree - see tripNumbering
      const displayNumbers = tripDisplayNumbers(group.trips);
      group.trips.forEach((points, tripIndex) => {
        const tripKey = `${group.dateKey}__${tripIndex}`;
        if (!selectedTripKeys.has(tripKey)) return;
        if (!includeNonTrips && isNonTrip(points)) return;
        trips.push({
          dateKey: group.dateKey,
          dayIndex: group.dayIndex,
          dayLabel: group.label,
          tripIndex,
          tripNumber: tripDisplayNumberOr(displayNumbers, tripIndex),
          color: group.color,
          points,
          // drawn now on the fixes we already have; the road-aligned path
          // replaces it in place as soon as it arrives
          path: resolvedPathsRef?.current.get(tripKey),
        });
      });
    });

    // the stacked comparison views, drawn under the real line
    if (!overlayLinesRef.current)
      overlayLinesRef.current = createRouteOverlayLines({ map });
    overlayLinesRef.current.setTrips(
      trips.map((trip) => ({
        tripKey: `${trip.dateKey}__${trip.tripIndex}`,
        color: trip.color,
      }))
    );
    overlayLinesRef.current.setModes(
      overlayRouteModes(mapRef.current?.routeViewsEnabled)
    );

    const tooltip = createTooltipController();
    const renderHandle = renderMultiDayRoutes({
      map,
      trips,
      lineStyle: resolveRouteLineStyle(mapRef.current?.routeViewsEnabled),
      tooltip,
      speedUnit,
      timezone,
      onHoverTrip: onMapHoverTrip,
      onTripPointClick,
    });
    renderHandleRef.current = renderHandle;
    hasRenderedRef.current = true;
    if (shouldFitBounds) renderHandle.fitBoundsIfAny();
    // This redraw wiped everything, so restore any open detail pins - and
    // show them straight away. A rebuilt handle starts with no record of the
    // detail animation having run, and the animation effect below is keyed
    // on detailedTripKey, which a redraw does not change: nothing is coming
    // to reveal them, so holding them back loses them entirely.
    //
    // Only here. The effect further down is the user actually opening a
    // trip, where the animation IS about to play and reveals them on its own
    // clock - asking for them immediately there puts every pin on the map
    // before the route starts drawing.
    renderHandle.setDetailedTrip(
      detailedTripKeyRef.current,
      filteredStatusCodesRef.current,
      true
    );
    renderHandle.setOverlapsEnabled(overlapsEnabledRef.current);

    // wait for tiles and the fitBounds move to settle, or the animation
    // plays over a map that's still loading
    let tornDown = false;
    const google = (window as any).google;
    const startAnimation = () => {
      if (tornDown) return;
      setIsAnimating(true);
      setDrawProgress(0);
      setReplayClock(null);
      setIsPaused(false);
      resetSpeed();
      // reported and speed-driven like Replay All, so the controls panel
      // over the reveal is a working one rather than a label
      const progress = throttleProgress((percent, clock, fastForward) => {
        setDrawProgress(percent);
        setReplayClock(clock ?? null);
        setIsFastForwarding(!!fastForward);
      });
      flushProgressRef.current = progress.flush;
      renderHandle
        .playDrawInAnimation(() => replaySpeedRef.current, progress.onProgress)
        .then(() => {
          setIsAnimating(false);
          setDrawProgress(null);
          setReplayClock(null);
          setIsFastForwarding(false);
        });
    };
    const idleListener = shouldFitBounds
      ? google?.maps?.event?.addListenerOnce(map, "idle", startAnimation)
      : null;
    if (!shouldFitBounds) startAnimation();

    cleanupRef.current = () => {
      tornDown = true;
      if (idleListener) google.maps.event.removeListener(idleListener);
      renderHandle.clear();
      // the comparison lines go with the routes they annotate; the layer
      // itself and its resolved geometry survive for the next draw
      overlayLinesRef.current?.clear();
      tooltip.destroy();
      renderHandleRef.current = null;
    };

    return () => cleanupRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateGroups,
    selectedTripKeys,
    enabled,
    isDataReady,
    paused,
    includeNonTrips,
    mapInstanceVersion,
    assetKey,
  ]);

  useEffect(() => {
    renderHandleRef.current?.setLineStyle(
      resolveRouteLineStyle(mapRef.current?.routeViewsEnabled)
    );
    overlayLinesRef.current?.setModes(
      overlayRouteModes(mapRef.current?.routeViewsEnabled)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeViewsVersion]);

  // the layer outlives redraws, so it is torn down with the page
  useEffect(
    () => () => {
      overlayLinesRef.current?.destroy();
      overlayLinesRef.current = null;
    },
    []
  );

  // one stacked view's geometry for one trip, as useRouteOverlayPaths
  // resolves it
  const applyOverlayPath = useCallback(
    (tripKey: string, mode: RouteRenderMode, path: TimedPathPoint[]) => {
      overlayLinesRef.current?.setPath(tripKey, mode, path);
    },
    []
  );

  // kept out of the draw effect so hovering doesn't redraw everything. a
  // trip in detail view outranks hover.
  useEffect(() => {
    renderHandleRef.current?.setHighlightedTrip(detailedTripKey ?? hoveredTripKey);
  }, [hoveredTripKey, detailedTripKey]);

  // toggling this is a styling/visibility change, not a redraw - the
  // top-bar Overlaps switch flips it live
  useEffect(() => {
    renderHandleRef.current?.setOverlapsEnabled(overlapsEnabled);
  }, [overlapsEnabled]);

  // focused trip's event pins and zoom. whether the pins are held back for
  // the animation to reveal is decided in setDetailedTrip.
  useEffect(() => {
    renderHandleRef.current?.setDetailedTrip(
      detailedTripKey,
      filteredStatusCodes
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailedTripKey, statusFilterKey(filteredStatusCodes)]);

  // opening a trip redraws its route slowly. keyed on detailedTripKey only,
  // so changing the event filter in there doesn't restart it.
  useEffect(() => {
    if (!detailedTripKey) {
      void renderHandleRef.current?.fitBoundsToTrips(
        Array.from(selectedTripKeys)
      );
      setIsDetailAnimating(false);
      setDetailProgress(null);
      setDetailClock(null);
      setDetailSpeedKph(null);
      setDetailDrawComplete(false);
      setIsPaused(false);
      resetSpeed();
      return;
    }
    resetSpeed();
    setIsDetailAnimating(true);
    setDetailProgress(0);
    setDetailClock(null);
    setDetailSpeedKph(null);
    setDetailDrawComplete(false);
    setIsPaused(false);
    const renderHandle = renderHandleRef.current;
    const progress = throttleProgress(
      (percent, clock, fastForward, speedKph) => {
        setDetailProgress(percent);
        setDetailClock(clock ?? null);
        setIsFastForwarding(!!fastForward);
        setDetailSpeedKph(speedKph ?? null);
      }
    );
    flushProgressRef.current = progress.flush;
    (async () => {
      // nothing drawn yet to animate - the draw puts this trip's finished
      // picture up when it lands. Returning with the flags still set left
      // the controls panel open over a draw that was never coming.
      if (!renderHandle) {
        setIsDetailAnimating(false);
        setDetailProgress(null);
        setDetailClock(null);
        setDetailSpeedKph(null);
        return;
      }
      await renderHandle.fitBoundsToTrip(detailedTripKey);
      await renderHandle.playDetailAnimation(
        detailedTripKey,
        () => replaySpeedRef.current,
        progress.onProgress
      );
      setIsDetailAnimating(false);
      setDetailProgress(null);
      setDetailClock(null);
      setDetailSpeedKph(null);
      setDetailDrawComplete(true);
      setIsFastForwarding(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailedTripKey, selectedTripKeys]);

  // The detail view keeps the controls panel up on the finished route, so
  // it needs something to replay. Deliberately not playTimelineReplay:
  // that one wipes every detail pin at the start for the whole-map replay
  // and has nothing to bring them back here, where detail mode never
  // turns off.
  const replayDetail = () => {
    const handle = renderHandleRef.current;
    const tripKey = detailedTripKeyRef.current;
    if (!handle || !tripKey || isDetailAnimating) return;
    setIsDetailAnimating(true);
    setDetailProgress(0);
    setDetailClock(null);
    setDetailSpeedKph(null);
    setIsPaused(false);
    setDetailDrawComplete(false);
    // the pins are all still sitting there from the draw that just
    // finished - hide them and forget it happened, or the animation below
    // finds nothing left to reveal
    handle.resetDetailPinsForReplay();
    const progress = throttleProgress(
      (percent, clock, fastForward, speedKph) => {
        setDetailProgress(percent);
        setDetailClock(clock ?? null);
        setIsFastForwarding(!!fastForward);
        setDetailSpeedKph(speedKph ?? null);
      }
    );
    flushProgressRef.current = progress.flush;
    handle
      .playDetailAnimation(
        tripKey,
        () => replaySpeedRef.current,
        progress.onProgress
      )
      .then(() => {
        setIsDetailAnimating(false);
        setDetailProgress(null);
        setDetailClock(null);
        setDetailSpeedKph(null);
        setDetailDrawComplete(true);
        setIsFastForwarding(false);
      });
  };

  const stopDetailAnimation = () => {
    renderHandleRef.current?.stopDetailAnimation();
    setIsDetailAnimating(false);
    setDetailProgress(null);
    setDetailClock(null);
    setDetailSpeedKph(null);
    setDetailDrawComplete(true);
    setIsFastForwarding(false);
    setIsPaused(false);
    resetSpeed();
  };

  // Replay All: every selected trip drawn against one real wall clock, so
  // the time in the panel is the time the head of the line is at. distinct
  // from the initial load (same draw, brisker pace) and from the
  // event-by-event "Replay events".
  const replay = () => {
    if (!renderHandleRef.current || isAnimating) return;
    resetSpeed();
    setIsAnimating(true);
    setIsReplayingAll(true);
    setReplayProgress(0);
    setReplayClock(null);
    setIsPaused(false);
    const progress = throttleProgress((percent, clock, fastForward) => {
      setReplayProgress(percent);
      setReplayClock(clock ?? null);
      setIsFastForwarding(!!fastForward);
    });
    flushProgressRef.current = progress.flush;
    renderHandleRef.current
      .playTimelineReplay(() => replaySpeedRef.current, progress.onProgress)
      .then(() => {
        setIsAnimating(false);
        setIsReplayingAll(false);
        setReplayProgress(null);
        setReplayClock(null);
        setIsFastForwarding(false);
      });
  };

  const stopReplay = () => {
    renderHandleRef.current?.stopTimelineReplay();
    setIsAnimating(false);
    setIsReplayingAll(false);
    setReplayProgress(null);
    setDrawProgress(null);
    setReplayClock(null);
    setIsFastForwarding(false);
    setIsPaused(false);
    resetSpeed();
  };

  // pauses whichever of Replay All or the detail draw is currently running
  // - the renderer's gate covers both
  const togglePause = () => {
    const handle = renderHandleRef.current;
    if (!handle) return;
    if (isPaused) {
      handle.resumeReplay();
      setIsPaused(false);
    } else {
      handle.pauseReplay();
      flushProgressRef.current();
      setIsPaused(true);
    }
  };

  // the page's route-view resolver calls this per trip as each road-aligned
  // path lands. A no-op before the renderer exists - the draw effect seeds
  // itself from resolvedPathsRef, so nothing resolved early is lost.
  const applyResolvedPath = useCallback(
    (tripKey: string, path: TimedPathPoint[], immediate?: boolean) => {
      renderHandleRef.current?.updateTripPath(tripKey, path, immediate);
    },
    []
  );

  // keeps the map's picked-event highlight in step with whatever the page
  // has selected - a list row, a map pin, or nothing
  const highlightEventPoint = useCallback(
    (point: VehicleMapDataPoint | null) => {
      renderHandleRef.current?.highlightEventPoint(point);
    },
    []
  );

  return {
    applyResolvedPath,
    applyOverlayPath,
    replay,
    highlightEventPoint,
    stopReplay,
    isAnimating,
    isReplayingAll,
    isPaused,
    togglePause,
    replaySpeed,
    setReplaySpeed,
    replayProgress,
    drawProgress,
    replayClock,
    isFastForwarding,
    isDetailAnimating,
    detailProgress,
    detailClock,
    detailSpeedKph,
    detailDrawComplete,
    replayDetail,
    stopDetailAnimation,
  };
};
